import { useCallback, useRef } from "react";
import { useBlocker, useNavigate } from "react-router-dom";

type BlockerState = "idle" | "blocked";

export const useUnsavedChangesWarning = (shouldBlock: boolean) => {
  const navigate = useNavigate();

  // isSkippingRef temporarily disables the blocker during proceed() so that
  // the retry navigation (which React Router fires internally after proceed())
  // does not re-block itself while shouldBlock is still true.
  const isSkippingRef = useRef(false);

  const blockerFn = useCallback(
    () => !isSkippingRef.current && shouldBlock,
    [shouldBlock]
  );

  // useBlocker intercepts ALL React Router navigation:
  // browser back/forward, programmatic navigate(), and <Link> clicks.
  const blocker = useBlocker(blockerFn);

  const state: BlockerState =
    blocker.state === "blocked" ? "blocked" : "idle";

  const proceed = useCallback(() => {
    if (blocker.state === "blocked") {
      isSkippingRef.current = true;
      blocker.proceed();
      isSkippingRef.current = false;
    }
  }, [blocker]);

  const reset = useCallback(() => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  }, [blocker]);

  // guardedNavigate is kept for API compatibility;
  // useBlocker intercepts the navigate call automatically
  const guardedNavigate = useCallback(
    (to: string) => {
      navigate(to);
    },
    [navigate]
  );

  return { state, proceed, reset, guardedNavigate };
};
